<template>
  <div class="heatmap" :class="{ 'heatmap--fill': fill }">
    <div class="heatmap-grid" :style="{ gridTemplateColumns: 'auto repeat(' + dayOrder.length + ', minmax(0, 1fr))' }">
      <div class="heatmap-corner"></div>
      <div v-for="d in dayOrder" :key="'HD' + d" class="heatmap-day">{{ dayNames[d] }}</div>
      <template v-for="h in hourRange">
        <div :key="'HH' + h" class="heatmap-hour">{{ formatHour(h) }}</div>
        <div
          v-for="d in dayOrder"
          :key="'HC' + d + '-' + h"
          class="heatmap-cell"
          :class="{ 'heatmap-cell--hot': cellValue(d, h) >= maxValue * 0.9 && cellValue(d, h) > 0 }"
          :style="cellStyle(d, h)"
          :title="cellTitle(d, h)"
        >
          <span v-if="cellValue(d, h) > 0" class="heatmap-cell-value" :style="valueStyle(d, h)">{{ Math.round(cellValue(d, h)) }}</span>
        </div>
      </template>
    </div>
    <div class="heatmap-legend">
      <span>0%</span>
      <span class="heatmap-legend-bar" :style="{ background: 'linear-gradient(to right, rgba(' + rgb + ', 0.07), rgba(' + rgb + ', 1))' }"></span>
      <span>{{ Math.round(maxValue) }}%</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HeatmapChart',
  props: {
    // Rows from the heatmap branch: { day_of_week (0 = Monday), hour_of_day, value (percent), booked, total }
    chartData: {
      type: Array,
      required: true,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // MySQL WEEKDAY order: 0 = Monday .. 6 = Sunday.
      dayNames: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      // Group setting: 0 = Sunday-first, 1 = Monday-first (calendar convention).
      firstDayOfWeek: Number(JSON.parse(localStorage.getItem('group')).first_day_of_week) || 0,
    };
  },
  computed: {
    rgb() {
      // Widget highlight color as "r, g, b" for rgba() mixes; falls back to the brand primary.
      const hex = /^#[0-9a-fA-F]{6}$/.test(this.color || '') ? this.color : '#e14eca';
      return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)).join(', ');
    },
    maxValue() {
      // Shade relative to the busiest cell so a venue that peaks at 30% still reads hot.
      return this.chartData.reduce((m, r) => Number(r.total) ? Math.max(m, Number(r.value) || 0) : m, 0) || 1;
    },
    cellByKey() {
      const map = new Map();
      for (const row of this.chartData) {
        map.set(Number(row.day_of_week) + '-' + Number(row.hour_of_day), row);
      }
      return map;
    },
    dayOrder() {
      // Convert the group's first day (0 = Sunday in the picker convention) into WEEKDAY indexes.
      const start = this.firstDayOfWeek === 0 ? 6 : this.firstDayOfWeek - 1; // Sunday = WEEKDAY 6
      return Array.from({ length: 7 }, (v, i) => (start + i) % 7);
    },
    hourRange() {
      // Only the hours that carry real volume. Empty hours (overnight dead zones) and noise
      // hours holding under 1% of the week's offered slots are dropped entirely so the active
      // hours get tall, readable rows.
      const totals = new Map();
      let weekTotal = 0;
      for (const r of this.chartData) {
        const t = Number(r.total) || 0;
        const h = Number(r.hour_of_day);
        totals.set(h, (totals.get(h) || 0) + t);
        weekTotal += t;
      }
      const floor = weekTotal * 0.01;
      return [...totals.keys()].filter(h => totals.get(h) > floor).sort((a, b) => a - b);
    },
  },
  methods: {
    formatHour(h) {
      const timeFormat = JSON.parse(localStorage.getItem('group')).time_format;
      if (timeFormat === 'HH:mm') return String(h).padStart(2, '0') + ':00';
      const twelve = h % 12 === 0 ? 12 : h % 12;
      return twelve + (h < 12 ? 'am' : 'pm');
    },
    cellValue(day, hour) {
      const row = this.cellByKey.get(day + '-' + hour);
      if (!row || !Number(row.total)) return 0;
      return Number(row.value);
    },
    cellStyle(day, hour) {
      const row = this.cellByKey.get(day + '-' + hour);
      if (!row || !Number(row.total)) {
        return { background: 'rgba(125, 125, 125, 0.06)' };
      }
      // Gamma curve so mid occupancy is clearly visible instead of washed out.
      const intensity = Math.pow(Math.min(1, Number(row.value) / this.maxValue), 0.7);
      return { background: 'rgba(' + this.rgb + ', ' + (0.12 + intensity * 0.88).toFixed(3) + ')' };
    },
    valueStyle(day, hour) {
      const intensity = Math.pow(Math.min(1, this.cellValue(day, hour) / this.maxValue), 0.7);
      return intensity > 0.35
        ? { color: 'rgba(255, 255, 255, 0.95)' }
        : { color: 'rgba(125, 125, 125, 0.9)' };
    },
    cellTitle(day, hour) {
      const row = this.cellByKey.get(day + '-' + hour);
      if (!row || !Number(row.total)) return this.dayNames[day] + ' ' + this.formatHour(hour) + ': no slots offered';
      return this.dayNames[day] + ' ' + this.formatHour(hour) + ': ' + row.value + '% occupancy (' + row.booked + ' of ' + row.total + ' slots booked)';
    },
  },
};
</script>

<style scoped>
.heatmap {
  width: 100%;
}
.heatmap--fill {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.heatmap-grid {
  display: grid;
  gap: 4px;
  flex: 1 1 auto;
  min-height: 0;
  grid-auto-rows: minmax(0, 1fr);
}
.heatmap-day,
.heatmap-hour {
  font-size: 10px;
  color: rgba(125, 125, 125, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}
.heatmap-hour {
  justify-content: flex-end;
  padding-right: 6px;
}
.heatmap-cell {
  border-radius: 4px;
  min-height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.1s ease;
}
.heatmap-cell:hover {
  transform: scale(1.06);
  outline: 1px solid rgba(125, 125, 125, 0.6);
}
.heatmap-cell--hot {
  box-shadow: 0 0 10px rgba(125, 125, 125, 0.45);
}
.heatmap-cell-value {
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
}
.heatmap-legend {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
  font-size: 9px;
  color: rgba(125, 125, 125, 0.9);
}
.heatmap-legend-bar {
  width: 90px;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(to right, rgba(225, 78, 202, 0.07), rgba(225, 78, 202, 1));
}
</style>
